import { memo, useCallback, useState } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components';
import {
  Button,
  Error,
  Field,
  Icon,
  Modal,
  SuccessStatus,
} from '@/components/generic';
import { zodResolver } from '@hookform/resolvers/zod';
import { useActivateCard } from '@package/smart-wallet/hooks';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const ActivateCardSchema = z.object({
  pin: z
    .string({ required_error: 'PIN is required' })
    .length(6, 'PIN must be exactly 6 digits')
    .regex(/^\d{6}$/, 'PIN must contain only digits'),
  activeCode: z
    .string({ required_error: 'Activation code is required' })
    .min(1, 'Activation code is required'),
  noPinPaymentAmount: z
    .string()
    .regex(/^\d+(\.\d+)?$/, 'Must be a valid numeric value')
    .optional()
    .or(z.literal('')),
});

type ActivateCardFormType = z.infer<typeof ActivateCardSchema>;

type ActivateCardModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  cardNo: string;
};

const ActivateCardModal = ({
  open,
  onClose,
  onSuccess,
  cardNo,
}: ActivateCardModalProps) => {
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const {
    handleActivateCard,
    isLoading,
    error: hookError,
  } = useActivateCard();

  const form = useForm<ActivateCardFormType>({
    resolver: zodResolver(ActivateCardSchema),
    defaultValues: {
      pin: '',
      activeCode: '',
      noPinPaymentAmount: '',
    },
  });

  const handleClose = useCallback(() => {
    if (!isLoading) {
      form.reset();
      setIsSuccess(false);
      onClose();
    }
  }, [isLoading, form, onClose]);

  const onSubmit = useCallback(
    async (data: ActivateCardFormType) => {
      try {
        const noPinAmount =
          data.noPinPaymentAmount && data.noPinPaymentAmount.length > 0
            ? data.noPinPaymentAmount
            : undefined;

        await handleActivateCard(
          cardNo,
          data.pin,
          data.activeCode,
          noPinAmount,
        );
        setIsSuccess(true);
        onSuccess();
      } catch {
        // Error handled by hook
      }
    },
    [handleActivateCard, onSuccess, cardNo],
  );

  return (
    <Modal open={open} onClose={handleClose}>
      {isSuccess ? (
        <SuccessStatus
          title={t('cards.physical_card.activate_success_title')}
          content={t('cards.physical_card.activate_success_message')}
          onClose={handleClose}
          confirm={handleClose}
        />
      ) : (
        <>
          <div className='mb-4 text-h4 max-md:text-h5 text-t-primary'>
            {t('cards.physical_card.activate_title')}
          </div>
          <p className='mb-2 text-body-2 text-t-secondary'>
            {t('cards.physical_card.activate_description')}
          </p>
          <div className='mb-6 p-3 bg-b-surface2 border border-s-stroke2 rounded-xl'>
            <div className='flex items-center gap-2 mb-1'>
              <span className='text-body-2 font-semibold text-t-primary'>
                💡 {t('cards.physical_card.activate_hint_title')}
              </span>
            </div>
            <p className='text-caption text-t-tertiary'>
              {t('cards.physical_card.activate_hint_message')}
            </p>
          </div>
          <Form {...form}>
            <form
              className='flex flex-col gap-4'
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <FormField
                control={form.control}
                name='pin'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Field
                        label={t('cards.physical_card.pin')}
                        type={showPin ? 'text' : 'password'}
                        placeholder='••••••'
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                        maxLength={6}
                        icon={
                          <button
                            type='button'
                            className='absolute top-1/2 right-4 -translate-y-1/2 text-t-secondary hover:text-t-primary transition-colors'
                            onClick={() => setShowPin((v) => !v)}
                          >
                            <Icon name='eye' className={showPin ? 'fill-t-primary' : 'fill-t-secondary'} />
                          </button>
                        }
                      />
                    </FormControl>
                    <FormMessage className='text-primary-03/80' />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='activeCode'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Field
                        label={t('cards.physical_card.activation_code')}
                        type='text'
                        placeholder='e.g. 12345678'
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage className='text-primary-03/80' />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='noPinPaymentAmount'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Field
                        label={`${t('cards.physical_card.no_pin_amount')} (${t('cards.physical_card.optional')})`}
                        type='text'
                        placeholder='e.g. 500'
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage className='text-primary-03/80' />
                  </FormItem>
                )}
              />

              <Error
                error={hookError ? true : false}
                message={hookError || t('cards.physical_card.activate_error')}
              />

              <div className='flex justify-end gap-3 mt-4'>
                <Button
                  className='flex-1'
                  isStroke
                  type='button'
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  {t('cards.physical_card.cancel')}
                </Button>
                <Button
                  className='flex-1'
                  isBlack={!isLoading}
                  isWhite={isLoading}
                  type='submit'
                  disabled={isLoading}
                >
                  {isLoading
                    ? t('cards.physical_card.activating')
                    : t('cards.physical_card.activate')}
                </Button>
              </div>
            </form>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default memo(ActivateCardModal);
